import React from "react";
import { Drawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import moment from "moment";
import { currencySymbol } from "shared/CurrencySymbol";
import { BookingStatus, canSeeContact, firstName, getAmountDue } from "../bookingStatus";
import { useReceiptDownload } from "../useReceiptDownload";

interface Props {
  opened: boolean;
  onClose: () => void;
  booking: any;
  status: BookingStatus;
  isHost: boolean;
  /** Slot for the pay section (added in the payment step). Not used for paid invoices. */
  footer?: React.ReactNode;
}

const Line = ({ label, value, tag, tone }: { label: React.ReactNode; value: string; tag?: string; tone?: "discount" }) => (
  <div
    className={`flex items-baseline justify-between gap-4 py-2.5 text-sm ${
      tone === "discount" ? "text-green-800 dark:text-green-300" : "text-neutral-800 dark:text-neutral-200"
    }`}
  >
    <span className="flex min-w-0 items-center gap-2">
      {tag && <span className="rounded-full bg-green-700 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white">{tag}</span>}
      <span className="min-w-0 break-words">{label}</span>
    </span>
    <span className="shrink-0 font-medium">{value}</span>
  </div>
);

const Fact = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div className="min-w-0">
      <dt className="text-xs text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="break-words text-[15px] font-semibold text-neutral-900 dark:text-neutral-100">{value}</dd>
    </div>
  ) : null;

/**
 * Read-only invoice (the "View full invoice" view). Bottom sheet on phones, right drawer on larger screens.
 * Uses the booking's own numbers, so it always matches the Price breakdown card.
 */
const InvoiceDetailsDrawer: React.FC<Props> = ({ opened, onClose, booking: b, status, isHost, footer }) => {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const { download, downloading, error, canDownload } = useReceiptDownload(b);
  const money = (n: number) => `${currencySymbol()}${(Number.isFinite(n) ? n : 0).toFixed(2)}`;

  const paid = status === "confirmed" || status === "payment_received";
  const confirmed = canSeeContact(status);
  const invoiceNo = b?.invoiceNo ?? b?.invoice?.id;

  const rent = Number(b?.item_total) || 0;
  const fee = Number(b?.service_tax) || 0;
  const extraGuests = Number(b?.extra_guest_amount) || 0;
  const tax = Number(b?.tax_total) || 0;
  const total = getAmountDue(b);
  const expenses: any[] = b?.invoice?.expenses || [];
  const discounts: any[] = b?.invoice?.discounts || [];

  const host = b?.property_user;
  const hostName = host?.first_name ? (confirmed && host.last_name ? `${host.first_name} ${host.last_name}` : host.first_name) : "Host";
  const guestName = b?.request_by ? (confirmed ? String(b.request_by) : firstName(b.request_by)) : "Guest";

  const fmt = (d: any) => {
    const m = moment(d, ["MMMM DD, YYYY", moment.ISO_8601]);
    return m.isValid() ? m.format("MMM D, YYYY") : "";
  };
  const paidOn = b?.completed_at ? fmt(b.completed_at) : "";
  const months = Number(b?.no_of_months) || 0;
  const shared = b?.is_shared_property === true || b?.is_shared_property === "true";
  const guests = Number(b?.number_of_guests) || 0;
  const meta = [
    shared ? b?.booked_room : null,
    `${fmt(b?.check_in_date)} – ${fmt(b?.check_out_date)}`,
    months ? `${months} ${months === 1 ? "month" : "months"}` : null,
    guests ? `${guests} ${guests === 1 ? "guest" : "guests"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position={isMobile ? "bottom" : "right"}
      size={isMobile ? "92%" : "min(480px, 100vw)"}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.6, blur: 2 }}
      classNames={{
        content: `!bg-white dark:!bg-neutral-900 ${isMobile ? "!rounded-t-2xl" : "border-l border-neutral-200 dark:border-neutral-700"}`,
        body: "!p-0",
      }}
    >
      <div className="flex flex-col text-neutral-900 dark:text-neutral-100" style={{ height: isMobile ? "92dvh" : "100dvh" }}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800 px-5 py-4 sm:px-7">
          <div className="min-w-0">
            <p className="text-[13px] text-neutral-500 dark:text-neutral-400">Invoice</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-3">
              <h2 className="text-2xl font-semibold leading-tight">{invoiceNo != null && invoiceNo !== "" ? `#${invoiceNo}` : "Invoice"}</h2>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                  paid
                    ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                    : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                }`}
              >
                {paid ? "Paid" : "Payment due"}
              </span>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close invoice"
            onClick={onClose}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-6 sm:px-7">
          {paid && (
            <div className="flex items-center gap-3 rounded-xl bg-green-50 dark:bg-green-900/20 px-4 py-3.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-700 text-white" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              <div>
                <p className="text-[15px] font-semibold text-green-800 dark:text-green-300">Payment received</p>
                {paidOn && <p className="text-[13px] text-green-800 dark:text-green-300">{paidOn}</p>}
              </div>
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Fact label="From" value={hostName} />
            <Fact label="Billed to" value={guestName} />
            <Fact label="Check-in" value={fmt(b?.check_in_date)} />
            <Fact label="Check-out" value={fmt(b?.check_out_date)} />
            {paid && <Fact label="Paid on" value={paidOn} />}
            <Fact label="Booking" value={b?.number} />
          </dl>

          <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800 px-4 py-3.5">
            <p className="text-[15px] font-semibold leading-snug">{b?.property?.title}</p>
            <p className="mt-1 text-[13px] text-neutral-500 dark:text-neutral-400">{meta}</p>
          </div>

          <div>
            <h3 className="mb-1 text-[15px] font-semibold">Charges</h3>
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {rent > 0 && <Line label={shared && b?.booked_room ? `First month · ${b.booked_room}` : "First month's rent"} value={money(rent)} />}
              {fee > 0 && <Line label="Service fee" value={money(fee)} />}
              {extraGuests > 0 && <Line label="Extra guests" value={money(extraGuests)} />}
              {tax > 0 && <Line label="Tax" value={money(tax)} />}
              {expenses.map((e, i) => (
                <Line key={`e${i}`} label={<span className="capitalize">{e.category}</span>} value={money(Number(e.amount))} />
              ))}
              {discounts.map((d, i) => (
                <Line key={`d${i}`} tag="DISCOUNT" tone="discount" label={<span className="capitalize">{d.description}</span>} value={`− ${money(Number(d.amount))}`} />
              ))}
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-4 border-t-2 border-neutral-900 dark:border-neutral-100 pt-4">
              <span className="text-base font-semibold">{paid ? (isHost ? "Guest paid" : "Total paid") : "Total due"}</span>
              <span className="text-[22px] font-semibold">{money(total)}</span>
            </div>

            {isHost && total > 0 && (
              <div className="mt-4 rounded-xl bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-800 dark:text-green-300">
                {fee > 0 && (
                  <div className="flex justify-between">
                    <span>Nomadroof service fee</span>
                    <span>− {money(fee)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold">
                  <span>You receive</span>
                  <span>{money(total - fee)}</span>
                </div>
              </div>
            )}
          </div>

          {footer}
        </div>

        {/* Footer */}
        {paid && canDownload && (
          <div
            className="shrink-0 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-5 pt-4 sm:px-7"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <button
              type="button"
              onClick={download}
              disabled={downloading}
              className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#C2410C] px-5 text-base font-semibold text-white transition-colors hover:bg-[#9A3412] disabled:opacity-60"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
              </svg>
              {downloading ? "Generating PDF..." : "Download receipt"}
            </button>
            {error && (
              <p role="alert" className="mt-2 text-center text-sm text-red-700 dark:text-red-400">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default InvoiceDetailsDrawer;
